import { SphereTooltip } from "@components/common/sphere-tooltip";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { neutral, Text } from "@flat-ui";
import { Box, Stack } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface TeamCategoryChipIconProps {
  /**
   * The category kind of the team.
   * Determines the styling and tooltip content.
   */
  kind: FaroCoreApiTypes.TeamCategoryKind;

  /** If shows the text instead of a dot icon. */
  shouldShowText?: boolean;
}

/**
 * Renders a category chip for a team, with optional label text and tooltip.
 *
 * - If `shouldShowText` is provided, the chip shows text in uppercase and pill-shaped.
 * - If `shouldShowText` is not provided, a small colored circle is shown instead.
 * - On hover, a tooltip provides information about the team category.
 */
export function TeamCategoryChipIcon({
  kind,
  shouldShowText = false,
}: TeamCategoryChipIconProps): JSX.Element {
  return (
    <SphereTooltip
      title={
        <Stack gap={"0.5rem"} padding={"0.375rem 0.75rem"}>
          <Text variant="bodyS" color={neutral[0]}>
            {teamDisplayProperties[kind].label}
          </Text>
          <Text variant="bodyS" color={neutral[0]}>
            {teamDisplayProperties[kind].description}
          </Text>
        </Stack>
      }
      tooltipProps={{
        slotProps: {
          tooltip: {
            sx: { backgroundColor: neutral[950] },
          },
        },
      }}
      enterDelay={300}
    >
      <Box
        sx={{
          height: "1.5rem",
          width: "fit-content",
          borderRadius: shouldShowText ? "1rem" : "50%",
          display: "flex",
          alignItems: "center",
          border: `1px solid ${teamDisplayProperties[kind].chipProps?.border}`,
          backgroundColor: teamDisplayProperties[kind].chipProps?.background,
          marginRight: shouldShowText ? 0 : "1rem",
          padding: "0 0.5rem",
        }}
      >
        {shouldShowText ? (
          <Text
            variant="labelS"
            color={teamDisplayProperties[kind].chipProps?.icon}
            textTransform={"uppercase"}
          >
            {teamDisplayProperties[kind].label}
          </Text>
        ) : (
          <Box
            data-testid="CircleIcon"
            sx={{
              backgroundColor: teamDisplayProperties[kind].chipProps?.icon,
              width: "0.5rem",
              height: "0.5rem",
              borderRadius: "0.25rem",
            }}
          />
        )}
      </Box>
    </SphereTooltip>
  );
}
